/**
 * Machine List Page
 * Inventário de máquinas da empresa (NR 12): filtros em linha, situação e pior risco de cada uma.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Chip, Tooltip, Typography, type ChipProps, type SxProps, type Theme } from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CheckCircle as OkIcon,
  ErrorOutline as RiskIcon,
  HourglassEmpty as PendingIcon,
  WarningAmber as WarnIcon,
} from '@mui/icons-material';
import {
  DataTable,
  DeleteWithDependenciesDialog,
  InlineFilterBar,
  InlineSelect,
  ControleIconButton,
  PageShell,
  RouteActionButton,
  type DataTableColumn,
} from '../../components';
import { pode } from '../../authorization/pode';
import { useListaPermitida } from '../../authorization/useListaPermitida';
import { useAcao, useControle } from '../../authorization/useVeredito';
import { useFilterState } from '../../hooks/useFilterState';
import { useDeleteWithDependencies } from '../../hooks/useDeleteWithDependencies';
import { departmentService } from '../../services/departmentService';
import {
  machineService,
  type HrnScaleBand,
  type MachineFilters,
  type MachineListItem,
  type MachineSituation,
} from '../../services/machineService';
import type { DependencyCheck } from '../../types/dependencyCheck';
import { dependencyItems } from '../../utils/dependencyItems';

/** Os setores do filtro; a lista só é pedida a quem pode ler setores (`useListaPermitida`). */
const loadDepartmentOptions = async (): Promise<readonly { value: string; label: string }[]> => {
  const response = await departmentService.getDepartments({ page: 1, limit: 1000 });
  return response.data.map((department) => ({ value: String(department.id), label: department.name }));
};

/** Opções do filtro que não são um nível da escala; o resto vem das faixas do servidor. */
const FILTER_HIGH_OR_WORSE = '__alto_ou_pior';
const FILTER_NO_APPRAISAL = '__sem_apreciacao';
const HIGH_LEVEL = 'Alto';

/** Atalhos do filtro de risco; "Alto ou pior" só existe se a escala do servidor tiver o nível "Alto". */
function hrnFilterOptions(bands: HrnScaleBand[]): { value: string; label: string }[] {
  const levels = bands.map((band) => ({ value: band.level, label: band.level }));
  const shortcuts = [{ value: FILTER_NO_APPRAISAL, label: 'Sem apreciação' }];
  if (bands.some((band) => band.level === HIGH_LEVEL)) {
    shortcuts.unshift({ value: FILTER_HIGH_OR_WORSE, label: 'Alto ou pior' });
  }
  return [...shortcuts, ...levels];
}

function hrnFilterToApi(value: string): MachineFilters {
  if (value === FILTER_HIGH_OR_WORSE) return { hrnLevelMin: HIGH_LEVEL };
  if (value === FILTER_NO_APPRAISAL) return { semApreciacao: true };
  return value ? { hrnLevel: value } : {};
}

/** Formata o HRN em pt-BR, com vírgula decimal (2,4) e sem zeros à toa. */
const formatHrn = (hrn: number): string => hrn.toLocaleString('pt-BR', { maximumFractionDigits: 2 });

const SITUATION_CHIPS: Record<
  MachineSituation,
  { label: string; color: Exclude<ToneColor, 'error'>; icon: React.ReactElement }
> = {
  EM_CADASTRO: { label: 'Em cadastro', color: 'info', icon: <PendingIcon /> },
  ADEQUAR: { label: 'Adequar', color: 'warning', icon: <WarnIcon /> },
  CONFORME: { label: 'Conforme', color: 'success', icon: <OkIcon /> },
};

type ToneColor = 'info' | 'warning' | 'success' | 'error';

/**
 * Chip legível: o texto e a borda usam o tom escuro da paleta (contraste acima de 4,5:1 sobre o
 * branco, o tom `main` do aviso e do sucesso fica em 3,2:1) e a letra sobe para 14px. No tema
 * escuro volta o tom claro, que é o que contrasta com o fundo escuro.
 */
const readableChipSx = (color: ToneColor): SxProps<Theme> => (theme) => ({
  fontSize: '0.875rem',
  fontWeight: 500,
  color: theme.palette[color].dark,
  borderColor: theme.palette[color].dark,
  '& .MuiChip-icon': { color: 'inherit' },
  ...theme.applyStyles('dark', {
    color: theme.palette[color].light,
    borderColor: theme.palette[color].light,
  }),
});

const MachineSituationChip: React.FC<{ situation: MachineSituation }> = ({ situation }) => {
  const { label, color, icon } = SITUATION_CHIPS[situation];
  return <Chip size="small" variant="outlined" icon={icon} label={label} sx={readableChipSx(color)} />;
};

/** Número HRN e palavra: o nível nunca é entregue só pela cor. A cor vem da posição da faixa na escala. */
const MachineHrnChip: React.FC<{ hrn: number | null; level: string | null; bands: HrnScaleBand[] }> = ({
  hrn,
  level,
  bands,
}) => {
  if (hrn === null || !level) {
    return <Chip size="small" variant="outlined" label="Sem apreciação" sx={{ fontSize: '0.875rem' }} />;
  }
  const position = bands.findIndex((band) => band.level === level);
  const color: ToneColor | null = position < 0 ? null : position < 3 ? 'success' : position < 5 ? 'warning' : 'error';
  const chipProps: ChipProps = color
    ? { icon: color === 'success' ? <OkIcon /> : <RiskIcon />, sx: readableChipSx(color) }
    : { sx: { fontSize: '0.875rem' } };
  return (
    <Chip size="small" variant="outlined" label={`HRN ${formatHrn(hrn)} · ${level}`} {...chipProps} />
  );
};

interface MachineFiltersData {
  search: string;
  departmentId: string;
  hrnLevel: string;
}

const DEFAULT_FILTERS: MachineFiltersData = { search: '', departmentId: '', hrnLevel: '' };

const buildMachineColumns = (bands: HrnScaleBand[]): DataTableColumn<MachineListItem>[] => [
  {
    id: 'name',
    label: 'Máquina',
    primary: true,
    render: (machine) => (
      <Box>
        <Typography variant="body1" fontWeight="medium">
          {machine.name}
        </Typography>
        {machine.tag && (
          <Typography variant="body2" color="text.secondary">
            {machine.tag}
          </Typography>
        )}
      </Box>
    ),
  },
  {
    id: 'maker',
    label: 'Fabricante e modelo',
    render: (machine) => [machine.manufacturer, machine.model].filter(Boolean).join(' ') || '-',
  },
  { id: 'serial', label: 'Nº de série', render: (machine) => machine.serialNumber ?? '-' },
  { id: 'protections', label: 'Proteções', render: (machine) => machine.protectionCount },
  { id: 'adequacies', label: 'Adequações abertas', render: (machine) => machine.openAdequacyCount },
  {
    id: 'hrn',
    label: 'Pior risco (HRN)',
    render: (machine) => <MachineHrnChip hrn={machine.worstHrn} level={machine.worstHrnLevel} bands={bands} />,
  },
  { id: 'situation', label: 'Situação', render: (machine) => <MachineSituationChip situation={machine.situation} /> },
];

export const MachineListPage: React.FC = () => {
  const navigate = useNavigate();
  const editar = useControle('/machines/:id/edit', pode('machine.update'), 'Editar');
  const excluir = useAcao('machine.delete', 'Excluir');
  const criar = useAcao('machine.create', 'Cadastrar a primeira máquina');

  const [machines, setMachines] = useState<MachineListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [apiFilters, setApiFilters] = useState<MachineFilters>({});
  const [bands, setBands] = useState<HrnScaleBand[]>([]);
  const departmentList = useListaPermitida('department.read', 'Setores', loadDepartmentOptions);
  const departments = useMemo(() => [...departmentList.itens], [departmentList.itens]);

  useEffect(() => {
    machineService
      .getHrnScale()
      .then((scale) => setBands(scale.bands))
      .catch(() => setBands([]));
  }, []);

  const hrnOptions = useMemo(() => hrnFilterOptions(bands), [bands]);

  const loadMachines = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await machineService.getMachines({ page: page + 1, limit: rowsPerPage, ...apiFilters });
      setMachines(response.machines);
      setTotal(response.total);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar máquinas');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, apiFilters]);

  useEffect(() => {
    loadMachines();
  }, [loadMachines]);

  const handleFilter = useCallback((data: MachineFiltersData) => {
    const filters: MachineFilters = {};
    if (data.search) filters.search = data.search;
    if (data.departmentId) filters.departmentId = Number(data.departmentId);
    setApiFilters({ ...filters, ...hrnFilterToApi(data.hrnLevel) });
    setPage(0);
  }, []);

  const { filters, setFilter, clearAll, activeChips } = useFilterState(DEFAULT_FILTERS, {
    onFilter: handleFilter,
    applyOnMount: true,
    live: true,
    labels: { departmentId: 'Setor', hrnLevel: 'Pior risco' },
    formatValue: {
      hrnLevel: (value: string) => hrnOptions.find((option) => option.value === value)?.label ?? value,
      departmentId: (value: string) => departments.find((option) => option.value === value)?.label ?? value,
    },
  });

  const filtersActive = Object.keys(apiFilters).length > 0;

  const { deleteTarget, deleteDependencies, handleDeleteClick, closeDeleteDialog, handleDeleteConfirm } =
    useDeleteWithDependencies<MachineListItem, DependencyCheck>({
      checkDependencies: (id) => machineService.checkDependencies(id),
      deleteEntity: (id) => machineService.deleteMachine(id),
      checkErrorMessage: 'Erro ao verificar vínculos da máquina',
      deleteErrorMessage: 'Erro ao excluir máquina',
      onError: setError,
      onDeleted: loadMachines,
    });

  // Ícones de 44px: o `actions` do DataTable é comum e fixa 30px, então a coluna de ações é daqui.
  const machineColumns = useMemo<DataTableColumn<MachineListItem>[]>(
    () => [
      ...buildMachineColumns(bands),
      {
        id: 'actions',
        label: 'Ações',
        align: 'center',
        render: (machine) => (
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
            <ControleIconButton
              controle={editar}
              onClick={() => navigate(`/machines/${machine.id}/edit`)}
              sx={{ width: 44, height: 44 }}
            >
              <EditIcon />
            </ControleIconButton>
            <ControleIconButton
              controle={excluir}
              color="error"
              onClick={() => handleDeleteClick(machine)}
              sx={{ width: 44, height: 44 }}
            >
              <DeleteIcon />
            </ControleIconButton>
          </Box>
        ),
      },
    ],
    [bands, editar, excluir, navigate, handleDeleteClick],
  );

  const goToNewMachine = () => navigate('/machines/new');

  return (
    <PageShell
      breadcrumbs={[{ label: 'Início', path: '/' }, { label: 'Máquinas' }]}
      title="Máquinas e equipamentos"
      subtitle="O inventário de cada empresa, com proteções, adequações e risco de cada máquina."
      actions={
        <RouteActionButton
          para="/machines/new"
          exige={pode('machine.create')}
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
        >
          Nova máquina
        </RouteActionButton>
      }
    >
      {error && !loading && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <InlineFilterBar
          searchPlaceholder="Nome, identificação ou nº de série"
          searchValue={filters.search}
          onSearchChange={(value) => setFilter('search', value)}
          resultCount={total}
          resultNoun="máquinas"
          chips={activeChips}
          onClearAll={clearAll}
          loading={loading}
        >
          <Tooltip title={departmentList.bloqueio ?? ''}>
            <span>
              <InlineSelect
                label="Setor"
                value={filters.departmentId}
                options={departments}
                onChange={(value) => setFilter('departmentId', value)}
                emptyLabel="Todos"
                minWidth={180}
                disabled={!departmentList.permitido}
              />
            </span>
          </Tooltip>
          <InlineSelect
            label="Pior nível de risco"
            value={filters.hrnLevel}
            options={hrnOptions}
            onChange={(value) => setFilter('hrnLevel', value)}
            emptyLabel="Todos"
            minWidth={200}
          />
        </InlineFilterBar>
      </Box>

      <DataTable
        columns={machineColumns}
        rows={machines}
        getRowId={(machine) => machine.id}
        loading={loading}
        filtersActive={filtersActive}
        onClearFilters={clearAll}
        emptyState={{
          title: 'Empresa sem máquinas',
          description:
            'O laudo NR 12 lista cada máquina com suas proteções e o risco de cada uma. Comece cadastrando a primeira; se o equipamento já está no catálogo de fontes geradoras, você escolhe o tipo e os dados vêm preenchidos.',
          action: {
            label: 'Cadastrar a primeira máquina',
            node: 'machines',
            deniedHint: 'Peça a um administrador para cadastrar as máquinas desta empresa.',
            onClick: goToNewMachine,
            allowed: !criar.disabled,
          },
        }}
        pagination={{
          page,
          rowsPerPage,
          totalCount: total,
          onPageChange: setPage,
          onRowsPerPageChange: (value) => {
            setRowsPerPage(value);
            setPage(0);
          },
        }}
      />

      <DeleteWithDependenciesDialog
        open={Boolean(deleteTarget)}
        loading={deleteDependencies === null}
        blocked={Boolean(deleteDependencies?.hasDependencies)}
        title="Excluir máquina"
        confirmText="Tem certeza que deseja excluir esta máquina? Proteções, adequações e perigos avaliados dela também saem."
        prerequisite={{
          title: 'Esta máquina está em uso',
          reason: 'Máquinas com vínculos não podem ser excluídas — remova os vínculos primeiro.',
          items: dependencyItems(deleteDependencies?.details),
          resolve: {
            label: 'Abrir a máquina',
            permission: 'machine.update',
            mode: 'navigate',
            route: `/machines/${deleteTarget?.id}/edit`,
          },
          deniedHint: 'Peça a um administrador para remover os vínculos desta máquina antes de excluí-la.',
        }}
        onClose={closeDeleteDialog}
        onConfirm={handleDeleteConfirm}
      />
    </PageShell>
  );
};

export default MachineListPage;
